import { Button as AntdButton, Col, Select } from 'antd';
import UsersDropdown from 'common/users/containers/users-dropdown-container';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, DatePicker, Flex, FormItem, notification, Row } from 'ui';
import moment from 'utils/cops-moment';
import Tags from '../../../common/tags/components/tags';
import { originatorTagPrefix, referenceTypes } from '../../../utils/app-configs';
import { checkErrorObject, isObjectNullOrEmpty } from '../../../utils/helpers';
import { category } from '../../ticket-config';
import ExtraTicketToolbarRow from './extra-ticket-toolbar-row';
import SnoozeUntilWarning from './snooze-until-warning';
import TicketToolbarHeader from './ticket-toolbar-header';

export const FormLabel = styled(FormItem)`
  min-height: 0px;
  .ant-legacy-form-item-label {
    flex: 0 0 28% !important;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  label {
    white-space: nowrap;
  }
`;

const FormLabelTags = styled(FormLabel)`
  .ant-legacy-form-item-label {
    margin-right: 5px !important;
  }
  .ant-legacy-form-item-control {
    line-height: 34px !important;
  }
`;

const StyledFormText = styled.p`
  line-height: 34px;
  font-size: 16px;
`;

const StyledDiv = styled.div`
  padding-top: 15px;
`;

export const StyledCol = styled(Col)`
  line-height: 34px;
  .ant-legacy-form-item-control-wrapper {
    margin-left: -10px;
    margin: auto;
  }
`;

const StyledUsersDropdown = styled(UsersDropdown)`
  width: 70%;
  .ant-select-search {
    height: 34px;
  }
`;

const SelectW100 = styled(Select)`
  font-size: 16px;
  font-weight: 400;
  width: 235px !important;
`;

const SnoozedUntil = styled.span`
  display: inline-block;
  margin-left: 10px;
  font-size: 14px;
`;

const SelectDatePicker = styled(DatePicker)`
  width: 235px;
  height: 32px;
`;


const StyledButton = styled(Button)`
  text-transform: uppercase;
  background-color: ${p => p.theme.colors.darkThemeSecondary} !important;
  color: #fff !important;
  margin-left: -60px;
`;

const OkButton = styled(AntdButton)`
  margin-top: 10px;
  margin-left: auto;
  width: fit-content;
  background-color: #6f7e95;
  color: white;
  &:hover {
    background-color: #6f7e95;
    color: white;
    opacity: 0.6;
    border: none !important;
  }
`;

const TicketToolbar = ({
  ticket,
  ticketId,
  context,
  snoozeTicket,
  intl,
  form,
  categoryInfo,
  setIsFormDirty,
  isNew,
  isDisabled,
  isSaving,
  isCommentsLoading,
  isLoading,
  fullPage,
  categories,
  dirty,
  setResetComment,
  setResetTags,
  resetTags,
  priorities,
  isLoadingCategoryInfo,
  resetLinkedObjectsAndWatchers,
  showSnoozeModal,
  parentType,
  save,
  isFormDirty,
  statuses,
  ticketStatusId,
  closingReasons = [],
  proposedActions = [],
  isStatusDisabled = false,
  isLoadingToolbarInfo = false,
}) => {
  const handleRecipientChange = () => {
    const { setFieldsValue } = form;
    setIsFormDirty(true);
    setFieldsValue({
      recepientId: null,
      recepientDisplayName: '',
    });
  };

  const isDiagnostics = ticket?.categoryId === category.diagnostics;

  useEffect(() => {
    form.setFieldsValue({
      priorityId: ticket.priorityId || categoryInfo?.priorities?.find(x => x.default)?.id || null,
    });
  }, [categoryInfo]);

  const handleRecipientSelect = ({ value, selectedUser }) => {
    const { setFieldsValue } = form;
    setFieldsValue({
      recepientId: value,
      recepientDisplayName: selectedUser.displayName,
    });
    setIsFormDirty(true);
  };

  const cancelForm = () => {
    form.resetFields();

    handleRecipientSelect({
      value: ticket.recepientId,
      selectedUser: {
        displayName: ticket.recepientDisplayName,
      },
    });
    resetLinkedObjectsAndWatchers();
    setResetComment(true);
    setResetTags(true);
    setIsFormDirty(false);
  };

  const handleSnoozeClick = () => {
    const tags = [
      ...form.getFieldValue('classificationTags'),
      ...form.getFieldValue('conclusionTags'),
    ];
    if (tags?.filter(t => originatorTagPrefix?.find(o => t.id.includes(o)))?.length === 1) {
      notification.destroy('snoozeWarning');
      if (ticket.isSnoozedUntil) {
        notification.warning({
          message: intl.formatMessage({ id: 'ticket.ALREADY_SNOOZED_TITLE' }),
          placement: 'top',
          description: <SnoozeUntilWarning
            id={ticketId}
            context={context}
            form={form}
            snoozeTicket={(params) => {
              if (isFormDirty) {
                save(() => snoozeTicket(params));
              } else {
                snoozeTicket(params);
              }
            }}
            isSnoozedUntil={ticket.isSnoozedUntil}
            intl={intl}
          />,
          className: 'extended-notification',
          style: {
            width: 500,
          },
          key: 'snoozeUntilWarning',
          duration: 0,
        });
      } else {
        showSnoozeModal();
      }
    } else {
      notification.warning({
        message: intl.formatMessage({ id: 'ticket.SNOOZE_WARNING_TITLE' }),
        description: <Flex ffcn>
          {intl.formatMessage({ id: 'ticket.SNOOZE_WARNING_DESCRIPTION' })}
          <OkButton onClick={() => notification.destroy('snoozeWarning')}>
            {intl.formatMessage({ id: 'common.OK' })}
          </OkButton>
        </Flex>,
        className: 'extended-notification',
        key: 'snoozeWarning',
        duration: 0,
        placement: 'top',
      });
    }
  };

  const recipientValidator = form.getFieldDecorator('recepientDisplayName', {
    initialValue: ticket.recepientDisplayName,
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.RECIPIENT' }) },
      {
        validator: (rule, value, callback) => {
          const selectedItem = form.getFieldValue('recepientId');
          if (!selectedItem && value) {
            callback(intl.formatMessage({ id: 'rules.RECIPIENT' }));
          } else {
            callback();
          }
        },
      },
    ],
  });
  const priorityValidator = form.getFieldDecorator('priorityId', {
    initialValue: ticket.priorityId || priorities?.find(x => x.default)?.id,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.PRIORITY' }) }],
  });
  const statusValidator = form.getFieldDecorator('statusId', {
    initialValue: ticketStatusId?.toString(),
  });
  const targetDateValidator = form.getFieldDecorator('targetDate', {
    initialValue: ticket.targetDate === '0001-01-01T00:00:00' ? null : moment(ticket.targetDate),
  });
  const errors = form?.getFieldsError();
  const hasError = !isObjectNullOrEmpty(errors) && !!checkErrorObject(errors)?.length;

  return (
    <StyledDiv>
      <TicketToolbarHeader
        ticket={ticket}
        form={form}
        isDisabled={isDisabled}
        isNew={isNew}
        isSaving={isSaving}
        dirty={dirty}
        hasError={hasError}
        isLoadingCategoryInfo={isLoadingCategoryInfo}
        isCommentsLoading={isCommentsLoading}
        isLoading={isLoading}
        save={save}
        fullPage={fullPage}
        cancelForm={cancelForm}
      />
      <Row type='flex' gutter={16} marginTop={16}>
        <Col flex='0 0 25%'>
          <FormLabel
            label={`${intl.formatMessage({ id: 'ticket.ASSIGNED' })}:`}
          >
            {recipientValidator(
              <StyledUsersDropdown
                onChange={handleRecipientChange}
                onSelect={handleRecipientSelect}
                disabled={isDisabled}
                placeholder={intl.formatMessage({ id: 'ticket.SEARCH_ASSIGNEE' })}
                allowClear={true}
                isRestricted={true}
                entityScope={ticket.scope}
              />,
            )}
          </FormLabel>
        </Col>
        <Col flex='0 0 25%'>
          <FormLabel
            label={`${intl.formatMessage({ id: 'ticket.PRIORITY_LABEL' })}:`}
          >
            {priorityValidator(
              <SelectW100
                dropdownMatchSelectWidth={true}
                disabled={isDisabled}
              >
                {priorities?.map(p =>
                  <Select.Option value={p.id.toString()} key={p.id}>
                    {p.description}
                  </Select.Option>,
                )}
              </SelectW100>,
            )}
          </FormLabel>
        </Col>
        <Col flex='0 0 25%'>
          <FormLabel
            label={`${intl.formatMessage({ id: 'ticket.CATEGORY' })}:`}
          >
            <StyledFormText>
              {categories?.find(c =>
                c.id === ticket.categoryId,
              )?.description || ''}
            </StyledFormText>
          </FormLabel>
        </Col>
        <Col flex='0 0 25%'>
          {!isNew &&
            parentType === referenceTypes.equipment &&
            isDiagnostics ?
            // eslint-disable-next-line
            <>
              <StyledButton dark={true} onClick={handleSnoozeClick}>
                {intl.formatMessage({ id: 'ticket.SNOOZE_SMARTLINK_EVENT' })}...
              </StyledButton>
              {ticket.isSnoozedUntil ?
                <SnoozedUntil>
                  {intl.formatMessage({ id: 'ticket.IS_SNOOZED_UNTIL' })}{': '}
                  {moment(ticket.isSnoozedUntil).format('L')}
                </SnoozedUntil> :
                null
              }
            </> :
            null
          }
        </Col>
      </Row>
      <Row gutter={16} marginTop={8}>
        <StyledCol flex='0 0 25%'>
          <FormLabel
            data-test-id='ticket-status-dropdown'
            label={`${intl.formatMessage({ id: 'ticket.STATUS_LABEL' })}:`}
          >
            {!isStatusDisabled ? statusValidator(
              <SelectW100 disabled={isNew || isLoadingToolbarInfo} >
                {statuses?.map(s =>
                  <Select.Option
                    disabled={s.isDisabled}
                    value={s.id.toString()}
                    key={s.id}
                    data-test-id={`ticket-dropdown-status-${s.description}`}
                  >
                    {s.description}
                  </Select.Option>,
                )}
              </SelectW100>,
            ) : statuses?.find(s => s.id === ticketStatusId)?.description}
          </FormLabel>
        </StyledCol>
        <Col flex='0 0 25%'>
          <FormLabel
            label={`${intl.formatMessage({ id: 'ticket.TARGET' })}:`}
          >
            {targetDateValidator(
              <SelectDatePicker allowClear={true} disabled={isDisabled} allowPast={false} />,
            )}
          </FormLabel>
        </Col>
        <StyledCol flex='0 0 50%'>
          {categoryInfo?.classificationTags &&
            ticket.categoryId &&
            <FormLabelTags
              label={`${intl.formatMessage({ id: 'ticket.TAGS' })}:`}
              labelCol={{ span: 3 }}
            >
              <Tags
                maxItemsToShow={3}
                selectedTags={ticket.classificationTags || []}
                allCategoryTags={categoryInfo?.classificationTags}
                initialSelectedTags={ticket.classificationTags || []}
                name='classificationTags'
                form={form}
                disabled={isDisabled}
                setFormDirty={() => setIsFormDirty(true)}
                resetTags={resetTags}
                categoryInfo={categoryInfo}
                ticket={ticket}
                isNew={isNew}
                // Don't use custom colors, see https://ant.design/components/tag/
                tagTextColor='blue'
                hideOverflow={true}
              />
            </FormLabelTags>
          }
        </StyledCol>
      </Row>
      <ExtraTicketToolbarRow
        isDisabled={isDisabled}
        data={{
          plannerGroup: ticket?.plannerGroup,
          closingReason: ticket?.closingReason,
          proposedActionId: ticket?.proposedActionId,
        }}
        metaData={{
          plant: ticket?.plant,
          closingReasonTranslations: closingReasons,
          proposedActions,
        }}
        form={form}
        isDiagnostics={isDiagnostics}
      />
    </StyledDiv >);
};

TicketToolbar.propTypes = {
  form: PropTypes.object,
  intl: PropTypes.object,
  ticket: PropTypes.object,
  categoryInfo: PropTypes.object,
  setIsFormDirty: PropTypes.func,
  isNew: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSaving: PropTypes.bool,
  isCommentsLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  fullPage: PropTypes.bool,
  categories: PropTypes.array,
  dirty: PropTypes.bool,
  setResetComment: PropTypes.func,
  setResetTags: PropTypes.func,
  resetTags: PropTypes.bool,
  isLoadingCategoryInfo: PropTypes.bool,
  resetLinkedObjectsAndWatchers: PropTypes.func,
  showSnoozeModal: PropTypes.func,
  parentType: PropTypes.string,
  save: PropTypes.func,
  ticketId: PropTypes.string,
  context: PropTypes.object,
  snoozeTicket: PropTypes.func,
  isFormDirty: PropTypes.bool,
  priorities: PropTypes.array,
  statuses: PropTypes.array,
  ticketStatusId: PropTypes.string,
  closingReasons: PropTypes.array,
  isStatusDisabled: PropTypes.bool,
  isLoadingToolbarInfo: PropTypes.bool,
  proposedActions: PropTypes.array,
};

export default injectIntl(TicketToolbar);
